import { useStyletron } from '@thanx/uikit/theme'
import { TypographyKeys } from '@thanx/uikit/theme/typography'
import Information from 'components/Icons/Information'
import palette from 'constants/palette'
import React from 'react'

type Action = {
  label: string
  // The words after the link, e.g. "to offer it." Kept on the action rather
  // than folded into `children`, because in the one v67 instance that has a
  // link (R1 sec 2.1) they come AFTER it, and only the action itself knows
  // where the link ends.
  suffix?: React.ReactNode
  onClick: () => void
}

type Props = {
  // The sentence, composed by the caller. Not owned here: each of the three
  // FR-58 consumer panes (Reply address, Guest experience, Notifications)
  // writes its own one-line copy, with its own <b>written feedback</b> where
  // R1 calls for one (secs 1.1/2.1/3.1), and ships it with its own PR.
  children: React.ReactNode
  // Only the nested Call-the-store note in Guest experience (R1 sec 2.1)
  // carries one; the three pane-level notes don't. Renders nothing when
  // omitted.
  action?: Action
  // So a consumer's own gated fieldset can point `aria-describedby` at this
  // note. Not wired up here — the real disabling happens where the control
  // lives, not in this presentational component.
  id?: string
}

// Measured off prototype v70's own `.sp-gate` rule. The text is NOT uikit's
// BodyText5 as drawn: that token is 0.75rem (12px) at weight 500, against the
// design's 12.5px at weight 400 with no tracking at all. The token still
// supplies everything else (family, style), so it is spread FIRST and these
// three values overwrite the three it gets wrong — one `css()` call, not a
// second class layered on top, because styletron's atomic classes carry equal
// specificity and the winner would otherwise depend on stylesheet order.
const TEXT_FONT_SIZE = '12.5px'
const TEXT_FONT_WEIGHT = 400
const TEXT_LETTER_SPACING = 'normal'
// `.sp-gate` carries its own top margin in the design, rather than leaning on
// whatever sits above it. In normal flow this collapses with the preceding
// element's larger bottom margin, so it changes nothing where a consumer
// already spaces the note; it is the design's own mechanism, kept here so the
// note is correctly spaced wherever it is mounted.
const MARGIN_TOP = '12px'

// FR-58's `.sp-gate` note (R1 secs 1.3/2.3): identical container, icon and
// text in the two places the design reuses it — standalone at the top of a
// pane, and nested inside a single card row. The two placements differ only
// in WHERE a consumer mounts this component, never in its markup or styling.
const WrittenFeedbackGateNote = ({ children, action, id }: Props) => {
  const [css, theme] = useStyletron()

  return (
    <div
      id={id}
      className={css({
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        marginTop: MARGIN_TOP,
        maxWidth: '620px',
        backgroundColor: palette.grey05,
        borderTopWidth: '1px',
        borderRightWidth: '1px',
        borderBottomWidth: '1px',
        borderLeftWidth: '1px',
        borderTopStyle: 'solid',
        borderRightStyle: 'solid',
        borderBottomStyle: 'solid',
        borderLeftStyle: 'solid',
        borderTopColor: palette.grey20,
        borderRightColor: palette.grey20,
        borderBottomColor: palette.grey20,
        borderLeftColor: palette.grey20,
        borderRadius: '8px',
        paddingTop: '10px',
        paddingRight: '13px',
        paddingBottom: '10px',
        paddingLeft: '13px',
      })}
    >
      {/* Decorative: the sentence beside it carries the meaning. */}
      <span
        aria-hidden="true"
        className={css({ display: 'flex', flexShrink: 0 })}
      >
        <Information type="info" fillColor={palette.grey70} size={15} />
      </span>
      <span
        className={css({
          ...theme.typography[TypographyKeys.BodyText5],
          color: palette.grey70,
          fontSize: TEXT_FONT_SIZE,
          fontWeight: TEXT_FONT_WEIGHT,
          letterSpacing: TEXT_LETTER_SPACING,
        })}
      >
        {children}
        {action && (
          <>
            {' '}
            <button
              type="button"
              onClick={action.onClick}
              className={css({
                borderTopWidth: '0',
                borderRightWidth: '0',
                borderBottomWidth: '0',
                borderLeftWidth: '0',
                borderTopStyle: 'none',
                borderRightStyle: 'none',
                borderBottomStyle: 'none',
                borderLeftStyle: 'none',
                marginTop: '0',
                marginRight: '0',
                marginBottom: '0',
                marginLeft: '0',
                paddingTop: '0',
                paddingRight: '0',
                paddingBottom: '0',
                paddingLeft: '0',
                backgroundColor: 'transparent',
                fontSize: 'inherit',
                fontFamily: 'inherit',
                lineHeight: 'inherit',
                color: palette.belizeHole,
                textDecoration: 'none',
                cursor: 'pointer',
              })}
            >
              {action.label}
            </button>
            {action.suffix && <> {action.suffix}</>}
          </>
        )}
      </span>
    </div>
  )
}

export default WrittenFeedbackGateNote
